import {
  Component,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
} from "@angular/core";
import { search, VAIconRegistryService } from "@va/core-library/icons";
import { forkJoin, Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { PharmaDataService } from "../pharma-data.service";
import { FacetFilter, extraInfoKeys } from "../pharma-page.model";
import { NzTreeComponent } from "ng-zorro-antd/tree";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { Store } from "@ngrx/store";
import {
  FilterEventChanged,
  UpdatePills,
} from "src/app/store/actions/pharma-page.actions";
import { ChartFilters } from "src/app/store/models/charts-page.model";
import { Dictionary } from "highcharts";

@Component({
  selector: "app-database-page-facet",
  templateUrl: "./database-page-facet.component.html",
  styleUrls: ["./database-page-facet.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class DatabasePageFacetComponent implements OnInit, OnDestroy {
  inputFacetFilter;
  @Output() facetFilterChanged: EventEmitter<any> = new EventEmitter();
  @ViewChildren(NzTreeComponent) nzTreeComponents!: QueryList<NzTreeComponent>;
  facetFilter: FacetFilter = new FacetFilter();
  lastFacetFilter: FacetFilter = new FacetFilter();
  panels = [];
  facetData = {};
  facetSearch = "";
  facetCategorySearch = {};
  cachedSelections = {};
  terminatedSwitch: boolean = false;
  placeholderValues: { [key: string]: string } = {};
  lastCachedSelections = {
    ticker: {},
    therapeutic_area: {},
    phase: {},
    api: {},
    drug: {},
    indication: [],
    moa: [],
    drug_class: [],
    target: [],
    commercial_status: [],
  };
  dataCallSubscription: Subscription;
  facetSearchSubscription: Subscription;
  facetCategorySearchSubscription: Subscription;
  pharmaDatabasePageSubscriber: Subscription;
  loading = false;
  activePanel = "";
  refreshCurrentPanel = false;
  customSkeletonConfig = {
    skeletonType: "list",
    config: {
      list: {
        noOfListItems: 8,
      },
    },
  };
  inputChartFilter: ChartFilters;
  appliedChartFilters: any = {};
  facetSearchChange: Subject<string> = new Subject<string>();
  facetCategorySearchChange: Subject<any> = new Subject<any>();
  searchResultOptions = {};
  searching = false;

  HIERARCHY_CATEGORIES = new Set(["indication", "moa", "drug_class", "target"]);
  constructor(
    private dataService: PharmaDataService,
    private iconRegistryService: VAIconRegistryService,
    private store: Store<AppState>,
  ) {
    this.iconRegistryService.registerIcons([search]);
  }

  ngOnInit() {
    this.panels = [
      {
        name: "Indication",
        active: false,
        key: "indication",
        isTree: true,
        useVirtualScroll: false,
        treeIndex: 0,
        showCount: true,
      },
      {
        name: "Clinical Phases (by Indication)",
        active: false,
        key: "phase",
        showCount: true,
      },
      {
        name: "Drug Commercial Status",
        active: false,
        key: "commercial_status",
        isTree: true,
        treeIndex: 4,
        useVirtualScroll: false,
        showCount: true,
      },
      {
        name: "MOA",
        active: false,
        key: "moa",
        isTree: true,
        treeIndex: 1,
        useVirtualScroll: true,
        showCount: true,
      },
      {
        name: "Target",
        active: false,
        key: "target",
        isTree: true,
        treeIndex: 2,
        showCount: true,
        useVirtualScroll: true,
      },
      {
        name: "Company",
        active: false,
        key: "ticker",
        useVirtualScroll: true,
        showCount: true,
      },
      {
        name: "Therapeutic Area",
        active: false,
        key: "therapeutic_area",
        showCount: true,
        useVirtualScroll: true,
      },
      {
        name: "Drug Class",
        active: false,
        key: "drug_class",
        isTree: true,
        treeIndex: 3,
        useVirtualScroll: true,
        showCount: true,
      },
      {
        name: "API",
        active: false,
        key: "api",
        useVirtualScroll: true,
        showCount: true,
      },
      {
        name: "Drug Name",
        active: false,
        key: "drug",
        useVirtualScroll: true,
        showCount: false,
      },
    ];

    this.panels.forEach((panel) => {
      this.placeholderValues[panel.key] = `Search ${panel.name}`;
    });
    this.pharmaDatabasePageSubscriber = this.store
      .select(stateGetter("pharmaDatabasePage"))
      .subscribe((data: any) => {
        this.inputChartFilter = data.chartFilters;
        this.facetFilter = FacetFilter.fromJson(data.facetFilters);
        this.lastFacetFilter = FacetFilter.fromJson(data.lastFacetFilter);
        if (data.filterEvent.filterChanged === "chart") {
          // update facets
          this.chartFilterChanged(
            this.inputChartFilter,
            data.pillsData.extraInfo,
          );
          return;
        }
        if (
          data.filterEvent.filterChanged === "facet" &&
          data.filterEvent.source === "default"
        ) {
          this.panels.forEach((p) => {
            this.facetCategorySearch[p.key] = "";
            this.facetData[p.key] = this.facetData[`${p.key}_org`];
          });
          this.callFacetApis(
            this.getFacetApiCalls(this.facetFilter),
            true,
            false,
            false,
            data.pillsData.extraInfo.fromQueryFilter
              ? null
              : data.pillsData.extraInfo,
          );
          this.lastFacetFilter = FacetFilter.fromJson(this.facetFilter.toDTO());
          this.lastCachedSelections = JSON.parse(
            JSON.stringify(this.cachedSelections),
          );
          return;
        }
        if (
          data.filterEvent.filterChanged === "pill" &&
          data.filterEvent.source === "delete"
        ) {
          const tagInfo = data.filterEvent.pillInfo;
          let category = tagInfo["key"];
          const id = tagInfo["id"];
          if (category === "company_id") {
            category = "ticker";
          }
          const panel = this.panels.find((p) => p.key === category);
          if (panel) {
            if (panel.isTree) {
              this.facetFilter[tagInfo["key"]].delete(id);
              const cachedSelectionsCopy = { ...this.cachedSelections };
              cachedSelectionsCopy[panel.key] = cachedSelectionsCopy[
                panel.key
              ].filter((data) => data !== id);
              this.cachedSelections = JSON.parse(
                JSON.stringify(cachedSelectionsCopy),
              );
            } else {
              this.facetFilter[tagInfo["key"]].delete(id);
              const cachedSelectionsCopy = { ...this.cachedSelections };
              delete cachedSelectionsCopy[panel.key][id];
              this.cachedSelections = JSON.parse(
                JSON.stringify(cachedSelectionsCopy),
              );
            }
          }
          this.callFacetApis(
            this.getFacetApiCalls(this.facetFilter),
            false,
            true,
            false,
            data.pillsData.extraInfo,
          );
          this.lastFacetFilter = FacetFilter.fromJson(this.facetFilter.toDTO());
          this.lastCachedSelections = JSON.parse(
            JSON.stringify(this.cachedSelections),
          );
          // fire pill delete event
          return;
        }
        if (
          data.filterEvent.source === "saved-filter" ||
          data.filterEvent.source === "search" ||
          data.filterEvent.source === "new-search"
        ) {
          const facetfilter = FacetFilter.fromJson(data.facetFilters);
          this.facetFilter = facetfilter;
          this.callFacetApis(
            this.getFacetApiCalls(this.facetFilter),
            true,
            false,
            false,
            data.pillsData.extraInfo.fromQueryFilter
              ? null
              : data.pillsData.extraInfo,
          );
          this.lastFacetFilter = FacetFilter.fromJson(this.facetFilter.toDTO());
          this.lastCachedSelections = JSON.parse(
            JSON.stringify(this.cachedSelections),
          );
          return;
        }
      });

    if (this.facetSearchSubscription) {
      this.facetSearchSubscription.unsubscribe();
    }
    this.facetSearchSubscription = this.facetSearchChange
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((keyword) => {
        keyword = keyword.trim();
        this.dataService.logUserAction("ALL_FACET_SEARCH", {
          query_string: keyword,
        });
        if (keyword.length >= 3) {
          this.searchAllFacetFilters(keyword);
          this.searching = false;
        } else {
          this.searchResultOptions = {};
        }
        this.searching = false;
      });

    if (this.facetCategorySearchSubscription) {
      this.facetCategorySearchSubscription.unsubscribe();
    }
    this.facetCategorySearchSubscription = this.facetCategorySearchChange
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(({ panel, value }) => {
        value = value.trim();
        this.dataService.logUserAction("FACET_CATEGORY_SEARCH", {
          query_string: value,
          category: panel.key,
        });
        if (value.length >= 3) {
          this.searchFacetCategory(panel, value);
        } else {
          this.facetData[panel.key] = this.facetData[`${panel.key}_org`];
        }
      });
  }

  onSwitchChange(event: boolean): void {
    //Terminated/Discontinued has hardcoded id 7 in BE.
    // this.activePanel = "include_terminated";
    this.activePanel = "phase";
    this.refreshCurrentPanel = !event;
    this.facetFilter.include_terminated = event;
    if (!this.facetFilter.include_terminated) {
      if (this.facetFilter.phase.has(7)) {
        this.facetFilter.phase.delete(7);
      }
      if (this.facetFilter.commercial_status.has(7)) {
        this.facetFilter.commercial_status.delete(7);
        this.facetData["commercial_status"][2].selected = false;
        this.facetData["commercial_status"][2].checked = false;
        this.cachedSelections = {
          ...this.cachedSelections,
          commercial_status: this.cachedSelections["commercial_status"].filter(
            (data) => data !== 7,
          ),
        };
      }
    }
    this.callFacetApis(this.getFacetApiCalls(this.facetFilter));
    this.lastFacetFilter = JSON.parse(
      JSON.stringify(FacetFilter.fromJson(this.facetFilter.toDTO())),
    );
    this.store.dispatch(
      new FilterEventChanged({
        filterChanged: "facet",
        source: "include_terminated",
        data: {
          facetFilters: this.facetFilter.toDTO(),
          extraInfo: this.dataService.getPharmaStateExtraInfo(
            extraInfoKeys,
            this.cachedSelections,
          ),
        },
      }),
    );
  }

  chartFilterChanged(chartFilters, pillExtraInfo) {
    this.removeChartFilters();
    this.applyChartFilters(chartFilters, pillExtraInfo);
    this.appliedChartFilters = { ...chartFilters };
    this.callFacetApis(this.getFacetApiCalls(this.facetFilter), false, true);
  }

  onExpandChange(param, panel) {
    //This is to circumvent a weird issue with nzTree. reference HIVE-4322
    param.node.canHide = false;
    // param.node.children.forEach((_) => _.canHide = false)
    if (
      param.node.isExpanded &&
      this.facetCategorySearch[panel.key] &&
      this.facetCategorySearch[panel.key].length >= 3
    ) {
      this.nzTreeComponents.toArray()[panel.treeIndex].nzSearchValue =
        this.facetCategorySearch[panel.key];
    }
  }

  updateFacetSelection() {
    this.cachedSelections = {
      ...this.cachedSelections,
      ticker: {},
      therapeutic_area: {},
      phase: {},
      api: {},
      drug: {},
      indication: [],
      moa: [],
      drug_class: [],
      target: [],
      commercial_status: [],
    };
    for (const category in this.facetFilter.toDTO()) {
      if (this.facetFilter[category].size > 0) {
        let key = category;
        if (category === "company_id") {
          key = "ticker";
        }
        const panel = this.panels.find((p) => p.key === key);

        const categoryData = this.facetData[`${key}_org`].filter((val) => {
          return this.facetFilter[category].has(val.value);
        });

        if (panel.isTree) {
          this.cachedSelections[key] = Array.from(this.facetFilter[category]);
          const res = [];
          this.searchHierarchyById(
            this.facetData[`${key}_org`],
            Array.from(this.facetFilter[category]),
            res,
            0,
          );
          res.forEach((data) => {
            if (!this.cachedSelections[`${key}_data`]) {
              this.cachedSelections[`${key}_data`] = {};
            }
            this.cachedSelections = {
              ...this.cachedSelections,
              [`${key}_data`]: {
                ...this.cachedSelections[`${key}_data`],
                [data.value]: data.title,
              },
            };
          });
        } else {
          categoryData.forEach((data) => {
            this.cachedSelections[key][data.value] = true;
            if (!this.cachedSelections[`${key}_data`]) {
              this.cachedSelections[`${key}_data`] = {};
            }
            this.cachedSelections = {
              ...this.cachedSelections,
              [`${key}_data`]: {
                ...this.cachedSelections[`${key}_data`],
                [data.value]: data.label,
              },
            };
            // this.cachedSelections[`${key}_data`][data.value] = data.label;
          });
        }
      }
    }
  }

  onOptionSelect(panel: any, option: any) {
    this.activePanel = panel.key;
    panel.active = true;
    let key = panel.key;
    if (panel.key === "ticker") {
      key = "company_id";
    }
    if (panel.isTree) {
      this.facetFilter[key].add(option.value);
      this.cachedSelections[panel.key].push(option.value);
    } else {
      this.facetFilter[key].add(option.value);
      this.cachedSelections = JSON.parse(
        JSON.stringify({
          ...this.cachedSelections,
          [panel.key]: { ...[panel.key], [option.value]: true },
        }),
      );
    }
    this.callFacetApis(this.getFacetApiCalls(this.facetFilter));
    this.lastFacetFilter = FacetFilter.fromJson(this.facetFilter.toDTO());
    this.lastCachedSelections = JSON.parse(
      JSON.stringify(this.cachedSelections),
    );

    if (!this.cachedSelections[`${panel.key}_data`]) {
      this.cachedSelections[`${panel.key}_data`] = {};
    }
    this.cachedSelections[`${panel.key}_data`] = {
      ...this.cachedSelections[`${panel.key}_data`],
      [option["value"]]: option["label"],
    };

    //Workaround for tree categories which do not reflect selections without a rerender
    if (panel.isTree && panel.key === this.activePanel) {
      this.activePanel = "";
      setTimeout(() => {
        this.activePanel = panel.key;
      }, 0);
    }

    this.facetSearch = "";
    this.store.dispatch(
      new FilterEventChanged({
        filterChanged: "facet",
        source: panel.key,
        data: {
          facetFilters: this.facetFilter.toDTO(),
          extraInfo: this.dataService.getPharmaStateExtraInfo(
            extraInfoKeys,
            this.cachedSelections,
          ),
        },
      }),
    );
  }

  onActiveChange() {
    this.panels.forEach((p) => {
      this.facetCategorySearch[p.key] = "";
      this.facetData[p.key] = this.facetData[`${p.key}_org`];
    });
  }

  onFacetSearch(event) {
    if (event.key === "Enter") {
      return;
    }
    this.searching = true;
    this.facetSearchChange.next(this.facetSearch);
  }

  onFacetCategorySearch(panel: any) {
    if (!panel.isTree) {
      this.facetCategorySearchChange.next({
        panel,
        value: this.facetCategorySearch[panel.key],
      });
    } else {
      this.dataService.logUserAction("FACET_CATEGORY_SEARCH", {
        query_string: this.facetCategorySearch[panel.key],
        category: panel.key,
      });
    }
  }

  searchAllFacetFilters(query: string) {
    query = query.toLowerCase();

    this.panels.forEach((panel) => {
      let res = [];
      if (panel.isTree) {
        //Fill res through reference using recursion
        this.searchFlattenedHierarchicalFacet(
          this.facetData[`${panel.key}_org`],
          query,
          panel.showCount,
          res,
        );
      } else {
        if (panel.key === "drug") {
          res = this.searchDrugWithAlias(
            this.facetData[`${panel.key}_org`],
            query,
          );
        } else {
          res = this.searchSingleValueFacet(
            this.facetData[`${panel.key}_org`],
            query,
            panel.showCount,
          );
        }
      }

      if (res.length) {
        this.searchResultOptions[panel.name] = res;
      } else {
        delete this.searchResultOptions[panel.name];
      }
    });

    this.searchResultOptions = { ...this.searchResultOptions };
    if (Object.keys(this.searchResultOptions).length === 0) {
      this.dataService.logUserAction("ALL_FACET_SEARCH_EMPTY", {
        query_string: query,
      });
    }
  }

  searchFacetCategory(panel: any, query: string) {
    query = query.toLowerCase();

    let res = [];
    if (panel.key === "drug") {
      res = this.searchDrugWithAlias(this.facetData[`${panel.key}_org`], query);
    } else {
      res = this.searchSingleValueFacet(
        this.facetData[`${panel.key}_org`],
        query,
        panel.showCount,
      );
    }

    if (res.length) {
      this.facetData[panel.key] = res;
    } else {
      this.facetData[panel.key] = [];
      this.dataService.logUserAction("FACET_CATEGORY_SEARCH_EMPTY", {
        query_string: query,
        category: panel.key,
      });
    }
  }

  searchSingleValueFacet(
    categoryData: Array<any>,
    query: string,
    withCount: boolean,
  ) {
    const res = categoryData.filter((data) => {
      if (withCount) {
        return (
          data["count"] > 0 &&
          (data["label"].toLowerCase().includes(query) ||
            (data["fullname"] &&
              data["fullname"].toLowerCase().includes(query)))
        );
      }
      return (
        data["label"].toLowerCase().includes(query) ||
        (data["fullname"] && data["fullname"].toLowerCase().includes(query))
      );
    });
    return res;
  }

  searchDrugWithAlias(drugData: Array<any>, query: string) {
    const res = [];
    drugData.forEach((data) => {
      if (data["label"].toLowerCase().includes(query)) {
        res.push({
          value: data.value,
          label: data.label,
        });
      } else {
        //If drug name doesn't match, try with alias name
        const aliases = data["alias"] ? data["alias"].split(";") : [];
        const matchingAlias = aliases.find((alias) =>
          alias.toLowerCase().includes(query),
        );
        if (matchingAlias) {
          res.push({
            value: data.value,
            label: data.label,
            alias: matchingAlias,
            isAlias: true,
          });
        }
      }
    });

    return res;
  }

  // searchHierarchicalFacet(
  //   hierachicalData: Array<any>,
  //   query: string
  // ) {
  //     let myRes = [];

  //     hierachicalData.forEach((data) => {
  //       const childRes = this.searchHierarchicalFacet(data["children"], query);

  //       if (childRes.length) {
  //         myRes.push({
  //           ...data,
  //           expanded: true,
  //           children: childRes
  //         });
  //       } else {
  //         if (data["title"].toLowerCase().includes(query)) {
  //           myRes.push({
  //             ...data,
  //             children: []
  //           });
  //         }
  //       }
  //     })

  //     return myRes;
  // }

  searchHierarchyById(
    hierarchicalData: Array<any>,
    ids: Array<number>,
    res: Dictionary<any>,
    count: number,
  ) {
    if (count === ids.length) {
      return;
    }
    for (let i = 0; i < hierarchicalData.length; i++) {
      const obj = hierarchicalData[i];
      if (ids.includes(obj["key"])) {
        res.push(obj);
        count++;
      }
      if (obj.children && obj.children.length) {
        this.searchHierarchyById(obj.children, ids, res, count);
      }
    }
  }

  searchFlattenedHierarchicalFacet(
    hierarchicalData: Array<any>,
    query: string,
    withCount: boolean,
    cache: Array<any>,
  ) {
    hierarchicalData.forEach((data) => {
      if (withCount) {
        if (data["count"] > 0 && data["key"].toLowerCase().includes(query)) {
          cache.push({
            value: data.value,
            label: data.key,
            count: data.count,
          });
        } else {
          //Dont process children if the parent itself is has no count
          if (data["count"] === 0) {
            return;
          }
        }
      } else {
        if (data["key"].toLowerCase().includes(query)) {
          cache.push({
            value: data.value,
            label: data.key,
          });
        }
      }
      if (data.children && data.children.length > 0) {
        this.searchFlattenedHierarchicalFacet(
          data.children,
          query,
          withCount,
          cache,
        );
      }
    });
  }

  removeChartFilters() {
    const chartFilters = this.appliedChartFilters;
    if (chartFilters["company_id"]) {
      this.cachedSelections["ticker"][chartFilters["company_id"]] = false;
    }
    if (chartFilters["therapeutic_area"]) {
      this.cachedSelections["therapeutic_area"][
        chartFilters["therapeutic_area"]
      ] = false;
    }
    if (chartFilters["phase"]) {
      this.cachedSelections = JSON.parse(
        JSON.stringify({
          ...this.cachedSelections,
          phase: {
            ...this.cachedSelections["phase"],
            [chartFilters["phase"]]: false,
          },
        }),
      );
    }
    if (chartFilters["indication"]) {
      //Chart filter clear scnario. Rmove last entry
      //Always the last entry will be chart filter since applying facet filter from UI will wipe out chart filter
      this.cachedSelections = JSON.parse(
        JSON.stringify({
          ...this.cachedSelections,
          indication: this.cachedSelections["indication"].slice(0, -1),
        }),
      );
    }
    if (chartFilters["moa"]) {
      //Chart filter clear scnario. Rmove last entry
      //Always the last entry will be chart filter since applying facet filter from UI will wipe out chart filter

      this.cachedSelections = JSON.parse(
        JSON.stringify({
          ...this.cachedSelections,
          moa: this.cachedSelections["moa"].slice(0, -1),
        }),
      );
    }

    if (chartFilters["target"]) {
      //Chart filter clear scnario. Rmove last entry
      //Always the last entry will be chart filter since applying facet filter from UI will wipe out chart filter

      this.cachedSelections = JSON.parse(
        JSON.stringify({
          ...this.cachedSelections,
          target: this.cachedSelections["target"].slice(0, -1),
        }),
      );
    }

    if (chartFilters["commercial_status"]) {
      //Chart filter clear scnario. Rmove last entry
      //Always the last entry will be chart filter since applying facet filter from UI will wipe out chart filter

      this.cachedSelections = JSON.parse(
        JSON.stringify({
          ...this.cachedSelections,
          target: this.cachedSelections["commercial_status"].slice(0, -1),
        }),
      );
    }
  }

  applyChartFilters(chartFilters, pillExtraInfo) {
    if (chartFilters["company_id"]) {
      this.cachedSelections = JSON.parse(
        JSON.stringify({
          ...this.cachedSelections,
          ticker: {},
        }),
      );
      this.cachedSelections["ticker"][chartFilters["company_id"]] = true;
      this.cachedSelections["ticker_data"] = { ...pillExtraInfo.ticker_data };
    }
    if (chartFilters["phase"]) {
      this.cachedSelections = JSON.parse(
        JSON.stringify({
          ...this.cachedSelections,
          phase: {},
        }),
      );
      this.cachedSelections["phase"][chartFilters["phase"]] = true;
      this.cachedSelections["phase_data"] = { ...pillExtraInfo.phase_data };
    }
    if (chartFilters["indication"]) {
      this.cachedSelections = JSON.parse(
        JSON.stringify({
          ...this.cachedSelections,
          indication: [],
        }),
      );
      this.cachedSelections["indication"].push(chartFilters["indication"]);
      this.cachedSelections["indication_data"] = {
        ...pillExtraInfo.indication_data,
      };
    }
    if (chartFilters["moa"]) {
      this.cachedSelections = JSON.parse(
        JSON.stringify({
          ...this.cachedSelections,
          moa: [],
        }),
      );
      this.cachedSelections["moa"].push(chartFilters["moa"]);
      this.cachedSelections["moa_data"] = { ...pillExtraInfo.moa_data };
    }
  }

  getFacetApiCalls(facetFilter) {
    const filter = {
      filter: facetFilter.toDTO(),
    };
    return [
      this.dataService.getFacetData(filter),
      this.dataService.getIndicationFacetData(filter),
      this.dataService.getMoaFacetData(filter),
      this.dataService.getDrugClassFacetData(filter),
      this.dataService.getTargetFacetData(filter),
    ];
  }

  callFacetApis(
    apiCalls,
    isRouteChangeCall?,
    updateSelections?,
    withWatchlist?,
    pillExtraInfo?,
  ) {
    //withWatchlist will always be false since it is out of scope for v1 release. Keeping it for future
    withWatchlist = false;
    if (withWatchlist) {
      apiCalls.push(this.dataService.getWatchlists());
    }

    if (this.dataCallSubscription) {
      this.dataCallSubscription.unsubscribe();
    }

    this.loading = true;

    this.dataCallSubscription = forkJoin(apiCalls).subscribe((resp) => {
      const facet = resp[0] as any;
      this.panels.forEach((panel) => {
        // Check panel key is not commercial_status
        if (panel.key !== "commercial_status") {
          this.facetData[panel.key] =
            panel.key === this.activePanel && !this.refreshCurrentPanel
              ? this.facetData[panel.key]
              : facet.data && facet.data[panel.key]
                ? facet.data[panel.key]
                : [];
          if (panel.key !== this.activePanel) {
            this.facetData[`${panel.key}_org`] = this.facetData[panel.key];
          }
        }
      });

      const commercialStatusFacet = facet.data["commercial_status"];
      this.facetData["commercial_status"] =
        "commercial_status" === this.activePanel && !this.refreshCurrentPanel
          ? this.facetData["commercial_status"]
          : commercialStatusFacet
            ? this.mapTreeData(commercialStatusFacet)
            : this.facetData["commercial_status"];
      this.facetData["commercial_status_org"] =
        this.facetData["commercial_status"];

      const indicationFacet = resp[1] as any;
      this.facetData["indication"] =
        "indication" === this.activePanel && !this.refreshCurrentPanel
          ? this.facetData["indication"]
          : indicationFacet.data
            ? this.mapTreeData(indicationFacet.data.values)
            : this.facetData["indication"];
      this.facetData["indication_org"] = this.facetData["indication"];

      const moaFacet = resp[2] as any;
      this.facetData["moa"] =
        "moa" === this.activePanel && !this.refreshCurrentPanel
          ? this.facetData["moa"]
          : moaFacet.data
            ? this.mapTreeData(moaFacet.data.values)
            : this.facetData["moa"];
      this.facetData["moa_org"] = this.facetData["moa"];

      const drugClassFacet = resp[3] as any;
      this.facetData["drug_class"] =
        "drug_class" === this.activePanel && !this.refreshCurrentPanel
          ? this.facetData["drug_class"]
          : drugClassFacet.data
            ? this.mapTreeData(drugClassFacet.data.values)
            : this.facetData["drug_class"];
      this.facetData["drug_class_org"] = this.facetData["drug_class"];

      const targetFacet = resp[4] as any;
      this.facetData["target"] =
        "target" === this.activePanel && !this.refreshCurrentPanel
          ? this.facetData["target"]
          : targetFacet.data
            ? this.mapTreeData(targetFacet.data.values)
            : this.facetData["target"];
      this.facetData["target_org"] = this.facetData["target"];

      if (withWatchlist) {
        const watchlists = resp[5] as any;
        const watchlistData: [] = watchlists.data.map((wl) => {
          return {
            label: wl.watchername,
            value: wl.watchername,
            isWatchlist: true,
            company_id: wl.company_info.map((_) => _.companyid),
          };
        });

        if (watchlistData.length) {
          this.facetData["ticker"] = watchlistData.concat(
            this.facetData["ticker"],
          );
        }
      }
      this.loading = false;
      this.activePanel = "";
      this.refreshCurrentPanel = false;
      if (isRouteChangeCall || updateSelections) {
        this.updateFacetSelection();
        if (pillExtraInfo) {
          this.cachedSelections = {
            ...this.cachedSelections,
            ...pillExtraInfo,
          };
        }
        this.store.dispatch(
          new UpdatePills({
            facetFilter: this.facetFilter.toDTO(),
            extraInfo: this.dataService.getPharmaStateExtraInfo(
              extraInfoKeys,
              this.cachedSelections,
            ),
          }),
        );
      }
    });
  }

  mapTreeData(data: Array<any>) {
    if (!data || !data.length) {
      return [];
    }
    const res = data.map((d) => {
      if (d.count === 0) {
        d.disabled = true;
        d.disableCheckbox = true;
      }
      return {
        ...d,
        title: `${d.title}`,
        children: this.mapTreeData(d.children),
      };
    });

    return res;
  }

  onOptionChange(category, options) {
    this.activePanel = category;
    let key = category;
    if (key === "ticker") {
      key = "company_id";
    }
    if (this.cachedSelections[category][options.value] !== undefined) {
      this.cachedSelections[category][options.value] =
        !this.cachedSelections[category][options.value];
    } else {
      this.cachedSelections = {
        ...this.cachedSelections,
        [category]: {
          ...this.cachedSelections[category],
          [options.value]: true,
        },
      };
    }
    if (this.cachedSelections[category][options.value]) {
      this.facetFilter[key].add(options["value"]);
    } else {
      this.panels = this.panels.map((panel) => {
        if (panel.key === category) {
          return {
            ...panel,
            allSelected: false,
          };
        }
        return panel;
      });
      this.facetFilter[key].delete(options["value"]);
    }
    this.callFacetApis(this.getFacetApiCalls(this.facetFilter));
    this.lastFacetFilter = JSON.parse(
      JSON.stringify(FacetFilter.fromJson(this.facetFilter.toDTO())),
    );

    if (!this.cachedSelections[`${category}_data`]) {
      this.cachedSelections[`${category}_data`] = {};
    }
    this.cachedSelections[`${category}_data`] = {
      ...this.cachedSelections[`${category}_data`],
      [options["value"]]: options["label"],
    };

    this.lastCachedSelections = JSON.parse(
      JSON.stringify(this.cachedSelections),
    );

    this.store.dispatch(
      new FilterEventChanged({
        filterChanged: "facet",
        source: category,
        data: {
          facetFilters: this.facetFilter.toDTO(),
          extraInfo: this.dataService.getPharmaStateExtraInfo(
            extraInfoKeys,
            this.cachedSelections,
          ),
        },
      }),
    );
  }

  onHierarchyOptionChange(category, option) {
    this.activePanel = category;
    this.cachedSelections = JSON.parse(
      JSON.stringify({ ...this.cachedSelections, [category]: option.keys }),
    );
    this.facetFilter[category] = new Set(option.keys);
    // if (option.keys.length !== this.facetData[`${category}_org`].length) {
    //   this.panels = this.panels.map((panel) => {
    //     if (panel.key === category) {
    //       return {
    //         ...panel,
    //         allSelected: false
    //       };
    //     }
    //     return panel;
    //   });
    // }

    this.callFacetApis(this.getFacetApiCalls(this.facetFilter));
    this.lastFacetFilter = JSON.parse(
      JSON.stringify(FacetFilter.fromJson(this.facetFilter.toDTO())),
    );

    if (!this.cachedSelections[`${category}_data`]) {
      this.cachedSelections[`${category}_data`] = {};
    }
    const dataObj = {};
    for (let i = 0; i < option.checkedKeys.length; i++) {
      const node = option.checkedKeys[i];
      const key = node.origin.key;
      const value = node.origin.title;
      dataObj[key] = value;
    }

    this.cachedSelections[`${category}_data`] = {
      ...this.cachedSelections[`${category}_data`],
      ...dataObj,
    };

    this.lastCachedSelections = JSON.parse(
      JSON.stringify(this.cachedSelections),
    );
    this.store.dispatch(
      new FilterEventChanged({
        filterChanged: "facet",
        source: category,
        data: {
          facetFilters: this.facetFilter.toDTO(),
          extraInfo: this.dataService.getPharmaStateExtraInfo(
            extraInfoKeys,
            this.cachedSelections,
          ),
        },
      }),
    );
  }

  ngOnDestroy() {
    if (this.dataCallSubscription) {
      this.dataCallSubscription.unsubscribe();
    }
    if (this.facetSearchSubscription) {
      this.facetSearchSubscription.unsubscribe();
    }
    if (this.facetCategorySearchSubscription) {
      this.facetCategorySearchSubscription.unsubscribe();
    }
    if (this.pharmaDatabasePageSubscriber) {
      this.pharmaDatabasePageSubscriber.unsubscribe();
    }
  }
}
